import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import useConfirm from "@/hooks/useConfirm";
import { renderQuoteRichText } from "@/components/quotes/QuoteRichTextField";
import QuotePdfPreview, { quotePreviewPropsFromQuote } from "@/components/quotes/QuotePdfPreview";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Eye, Copy, Edit3, FileText, Check, X, Clock,
  MoreHorizontal, Loader2, UserCheck, Mail, MessageSquare, StickyNote,
  CheckCircle2, XCircle, ChevronRight, Sparkles
} from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: "Draft", bg: "var(--gray-light)", color: "var(--gray)", icon: FileText },
  archived: { label: "Archived", bg: "#F3F4F6", color: "#6B7280", icon: FileText },
  pending_approval: { label: "Pending approval", bg: "var(--amber-light, #fff4d6)", color: "#a16207", icon: Clock },
  approved: { label: "Approved", bg: "var(--green-light)", color: "var(--green)", icon: CheckCircle2 },
  sent: { label: "Sent", bg: "var(--blue-light)", color: "var(--blue)", icon: Send },
  viewed: { label: "Viewed", bg: "#f3e8ff", color: "#7c3aed", icon: Eye },
  accepted: { label: "Accepted", bg: "var(--green-light)", color: "var(--green)", icon: CheckCircle2 },
  rejected: { label: "Rejected", bg: "var(--red-light)", color: "var(--red)", icon: XCircle },
  expired: { label: "Expired", bg: "var(--amber-light)", color: "var(--amber)", icon: Clock },
};

const STATUS_STEPS = ["Created", "Sent", "Viewed", "Responded"];

export default function QuoteDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, user } = useAuth();
  const confirmDialog = useConfirm();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAcceptPrompt, setShowAcceptPrompt] = useState(false);
  // Holds the inline preview's download() so the header button produces the
  // exact same file as the edit-screen preview.
  const previewApiRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/quotes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setQuote(res.data);
      } catch { toast.error("Quote not found"); navigate("/quotes/list"); }
      finally { setLoading(false); }
    };
    load();
  }, [id, token, navigate]);

  const isArchived = quote?.status === "archived";
  const restoreQuote = async () => {
    if (!(await confirmDialog({ title: "Restore quote?", body: `Restore quote ${quote.quote_number || ""} '${quote.title || "Untitled"}'? It goes back to its list with its previous status.`, confirmLabel: "Restore", testid: "restore-confirm-dialog" }))) return;
    try {
      const r = await axios.post(`${API}/quotes/${id}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setQuote(r.data); toast.success("Quote restored");
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const updateStatus = async (action) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/quotes/${id}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setQuote(res.data);
      toast.success(`Quote ${action === "send" ? "sent" : action === "accept" ? "accepted" : "rejected"}`);
    } catch { toast.error("Action failed"); }
    finally { setActionLoading(false); }
  };

  const handleAccept = () => setShowAcceptPrompt(true);

  const confirmAccept = async (moveLead) => {
    setActionLoading(true);
    setShowAcceptPrompt(false);
    try {
      if (moveLead && quote.lead_id) {
        const res = await axios.post(`${API}/quotes/${id}/convert-to-client`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setQuote(res.data.quote || res.data);
        toast.success("Quote accepted & lead moved to Closed Won");
      } else {
        await updateStatus("accept");
      }
    } catch { toast.error("Action failed"); }
    finally { setActionLoading(false); }
  };

  const duplicateQuote = async () => {
    try {
      const res = await axios.post(`${API}/quotes/${id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.quote_number} created as copy of ${quote.quote_number}`);
      navigate(`/quotes/${res.data.id}/edit`);
    } catch { toast.error("Failed to duplicate"); }
  };

  const sendViaComms = (mode = "email") => {
    sessionStorage.setItem("quote_context", JSON.stringify({ quote, mode }));
    navigate("/communications");
  };

  if (loading || !quote) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 52px)" }}>
        <Loader2 style={{ width: 24, height: 24, animation: "spin 0.8s linear infinite", color: "var(--blue)" }} />
      </div>
    );
  }

  const cfg = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
  const daysUntilExpiry = quote.valid_until ? Math.ceil((new Date(quote.valid_until) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;

  // Status tracker progress
  const statusMap = { draft: 0, sent: 1, viewed: 2, accepted: 3, rejected: 3 };
  const currentStep = statusMap[quote.status] ?? 0;
  const stepDates = [
    quote.created_at ? new Date(quote.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
    quote.sent_at ? new Date(quote.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
    quote.viewed_at ? new Date(quote.viewed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
    quote.accepted_at ? new Date(quote.accepted_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : quote.rejected_at ? new Date(quote.rejected_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
  ];

  // Activity timeline
  const activities = [];
  if (quote.created_at) activities.push({ time: quote.created_at, label: "Quote created", color: "var(--gray)" });
  if (quote.submitted_at) activities.push({ time: quote.submitted_at, label: `Submitted for approval${quote.submitted_by_name ? ` by ${quote.submitted_by_name}` : ""}`, color: "#7c3aed" });
  if (quote.approved_at) activities.push({ time: quote.approved_at, label: `Approved${quote.approved_by_name ? ` by ${quote.approved_by_name}` : ""}`, color: "var(--green)" });
  if (quote.changes_requested_at) activities.push({ time: quote.changes_requested_at, label: `Changes requested${quote.changes_requested_by ? ` by ${quote.changes_requested_by}` : ""}${quote.changes_feedback ? ` — "${quote.changes_feedback}"` : ""}`, color: "var(--amber)" });
  // Iter 183 (Iratzi #305838b7) — full send history. Each entry maps
  // to its own activity row so the rep can see every email out
  // (resends included) with sender + recipient + channel.
  for (const h of (quote.send_history || [])) {
    if (!h?.sent_at) continue;
    const sender = h.sent_by_name || h.sent_by_email || "rep";
    const recipient = h.recipient_email ? ` → ${h.recipient_email}` : "";
    const ch = h.channel && h.channel !== "email" ? ` [${h.channel}]` : "";
    activities.push({ time: h.sent_at, label: `Quote sent by ${sender}${recipient}${ch}`, color: "var(--blue)" });
  }
  // Fallback to the legacy sent_at marker if no history is recorded
  // yet (older quotes that pre-date Iter 183).
  if ((quote.send_history || []).length === 0 && quote.sent_at) {
    activities.push({ time: quote.sent_at, label: `Quote sent to ${quote.contact_email || "recipient"}`, color: "var(--blue)" });
  }
  if (quote.viewed_at) activities.push({ time: quote.viewed_at, label: "Quote viewed by recipient", color: "#7c3aed" });
  if (quote.accepted_at) activities.push({ time: quote.accepted_at, label: "Quote accepted", color: "var(--green)" });
  if (quote.rejected_at) activities.push({ time: quote.rejected_at, label: "Quote rejected", color: "var(--red)" });
  activities.sort((a, b) => new Date(a.time) - new Date(b.time));

  return (
    <div data-testid="quote-detail-page" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 52px)" }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderBottom: "0.5px solid var(--border)", background: "var(--surface)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate("/quotes")} className="btn" style={{ padding: "4px 8px", fontSize: 11 }} data-testid="back-to-quotes">
            <ArrowLeft style={{ width: 14, height: 14 }} /> Back
          </button>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>{quote.quote_number}</span>
              <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{quote.title}</span>
              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
              {quote.based_on && (
                <span style={{ fontSize: 9, color: "var(--text-tertiary)", fontStyle: "italic" }}>Based on {quote.based_on}</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10, color: "var(--text-tertiary)", marginTop: 2 }}>
              <span>Created {new Date(quote.created_at).toLocaleDateString()}</span>
              {quote.valid_until && (
                <span style={{ color: isExpired ? "var(--red)" : daysUntilExpiry <= 7 ? "var(--red)" : daysUntilExpiry <= 14 ? "var(--amber)" : "var(--text-tertiary)" }}>
                  Valid until {new Date(quote.valid_until).toLocaleDateString()}
                  {isExpired ? " (expired)" : daysUntilExpiry <= 7 ? ` (${daysUntilExpiry}d left)` : ""}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {/* Download the SAME faithful document shown in the inline preview
              (and on the edit screen) — one quote document everywhere. */}
          <button
            onClick={() => previewApiRef.current?.download()}
            className="btn"
            style={{ fontSize: 11, padding: "5px 12px", display: "inline-flex", alignItems: "center", gap: 4 }}
            data-testid="download-quote-pdf"
          >
            <FileText style={{ width: 12, height: 12 }} /> Download
          </button>
          {!isArchived && <button onClick={() => navigate(`/quotes/${id}/edit`)} className="btn" style={{ fontSize: 11, padding: "5px 12px" }} data-testid="edit-quote-btn">
            <Edit3 style={{ width: 12, height: 12 }} /> Edit
          </button>}
          <button onClick={duplicateQuote} className="btn" style={{ fontSize: 11, padding: "5px 12px" }} data-testid="duplicate-quote-btn">
            <Copy style={{ width: 12, height: 12 }} /> Duplicate
          </button>
          {(quote.status === "draft" || quote.status === "approved" || quote.status === "sent") && (
            <button onClick={() => sendViaComms("email")} className="btn btn-primary" style={{ fontSize: 11, padding: "5px 12px" }} data-testid="send-via-email">
              <Send style={{ width: 12, height: 12 }} /> {quote.status === "sent" ? "Resend" : "Send"}
            </button>
          )}
        </div>
      </div>
      {isArchived && (
        <div data-testid="quote-archived-banner" style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 12px", padding: "10px 14px", borderRadius: 8, background: "#F3F4F6", border: "0.5px solid #E5E7EB", fontSize: 12, color: "#374151" }}>
          <span style={{ flex: 1 }}>
            <b>Archived</b>{quote.archived_by_name ? ` by ${quote.archived_by_name}` : ""}{quote.archived_at ? ` on ${new Date(quote.archived_at).toLocaleString()}` : ""}. This quote is read-only until it's restored.
          </span>
          {["super_admin", "crm_admin", "admin"].includes(user?.role) && (
            <button data-testid="quote-restore-btn" className="btn" style={{ fontSize: 11, padding: "5px 12px" }} onClick={restoreQuote}>Restore</button>
          )}
        </div>
      )}

      {/* Iter 140.6 — Pending approval banner. Reps land here after */}
      {/* "Submit for approval" — they should NOT see send buttons until */}
      {/* the contract desk approves. */}
      {quote.status === "pending_approval" && (
        <div data-testid="pending-approval-banner" style={{
          background: "var(--amber-light, #fff4d6)", borderBottom: "0.5px solid #f0c060",
          padding: "10px 20px", display: "flex", alignItems: "center", gap: 10,
        }}>
          <Clock style={{ width: 16, height: 16, color: "#a16207" }} />
          <div style={{ fontSize: 12 }}>
            <strong style={{ color: "#a16207" }}>Submitted for approval.</strong>{" "}
            <span style={{ color: "var(--text-secondary)" }}>
              You'll be notified once the contract desk approves this quote. Sending is locked until then.
            </span>
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left — Quote document */}
        {/* Left — Quote document: the SINGLE approved preview, rendered
            inline from the saved quote so it matches the edit screen exactly. */}
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          <div style={{ maxWidth: 860, margin: "0 auto" }}>
            <QuotePdfPreview
              inline
              onReady={(api) => { previewApiRef.current = api; }}
              {...quotePreviewPropsFromQuote(quote, user)}
            />
            {quote.internal_notes && (
              <div data-testid="quote-detail-internal-notes" style={{ padding: 14, background: "#fffbeb", border: "1px solid var(--amber-light, #fde68a)", borderRadius: 8, marginTop: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--amber, #b45309)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Internal Notes · not visible to client</div>
                {renderQuoteRichText(quote.internal_notes, { fontSize: 11, color: "#92400e", lineHeight: 1.6 })}
              </div>
            )}
          </div>
        </div>

        {/* Right — Activity & Actions */}
        <div style={{ width: 300, flexShrink: 0, borderLeft: "0.5px solid var(--border)", overflow: "auto", padding: 16, background: "var(--page-bg)" }}>
          {/* Status tracker */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 10 }}>Status</div>
            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              {STATUS_STEPS.map((step, i) => {
                const reached = i <= currentStep;
                return (
                  <div key={step} style={{ flex: 1, textAlign: "center", position: "relative" }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: "50%", margin: "0 auto 4px",
                      background: reached ? "var(--green)" : "var(--gray-light)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {reached && <Check style={{ width: 10, height: 10, color: "#fff" }} />}
                    </div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: reached ? "var(--text-primary)" : "var(--text-tertiary)" }}>{step}</div>
                    <div style={{ fontSize: 7, color: "var(--text-tertiary)" }}>{stepDates[i] || "—"}</div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div style={{
                        position: "absolute", top: 10, left: "60%", right: "-40%", height: 2,
                        background: i < currentStep ? "var(--green)" : "var(--gray-light)",
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Activity timeline */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 10 }}>Activity</div>
            <div style={{ position: "relative", paddingLeft: 16 }}>
              <div style={{ position: "absolute", left: 5, top: 0, bottom: 0, width: 1, background: "var(--border)" }} />
              {activities.map((a, i) => (
                <div key={i} style={{ position: "relative", marginBottom: 12 }}>
                  <div style={{ position: "absolute", left: -14, top: 3, width: 8, height: 8, borderRadius: "50%", background: a.color, border: "2px solid var(--surface)" }} />
                  <div style={{ fontSize: 11, fontWeight: 500, color: "var(--text-primary)" }}>{a.label}</div>
                  <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{new Date(a.time).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick actions */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 10 }}>Actions</div>
            {/* Iter 176.8 (Iratzi — issue 56801e60) — gate send/SMS/accept-reject
                actions on quote.status so a pending_approval / rejected /
                expired quote can't be shipped to the customer prematurely.
                Approval workflow: draft → pending_approval → approved → sent.
                Only `approved`, `sent`, and `viewed` may be sent. */}
            {(() => {
              const SENDABLE = new Set(["approved", "sent", "viewed"]);
              const TERMINAL = new Set(["accepted", "rejected", "expired"]);
              const canSend = SENDABLE.has(quote.status);
              const isPendingApproval = quote.status === "pending_approval";
              const isTerminal = TERMINAL.has(quote.status);
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {isPendingApproval && (
                    <div data-testid="action-pending-approval-warn" style={{ padding: "8px 10px", background: "#fef3c7", border: "0.5px solid #f59e0b", borderRadius: 6, fontSize: 10.5, color: "#92400e", marginBottom: 4 }}>
                      Awaiting approval — send + accept actions are locked until a manager approves this quote.
                    </div>
                  )}
                  {isTerminal && (
                    <div data-testid="action-terminal-warn" style={{ padding: "8px 10px", background: "#f1f5f9", border: "0.5px solid #cbd5e0", borderRadius: 6, fontSize: 10.5, color: "#475569", marginBottom: 4 }}>
                      This quote is {quote.status} — read-only.
                    </div>
                  )}
                  <button onClick={() => sendViaComms("email")} disabled={!canSend} className="btn" style={{ width: "100%", fontSize: 11, justifyContent: "flex-start", padding: "8px 12px", opacity: canSend ? 1 : 0.5, cursor: canSend ? "pointer" : "not-allowed" }} data-testid="action-send-email">
                    <Mail style={{ width: 12, height: 12 }} /> Send quote via email
                  </button>
                  <button onClick={() => sendViaComms("sms")} disabled={!canSend} className="btn" style={{ width: "100%", fontSize: 11, justifyContent: "flex-start", padding: "8px 12px", opacity: canSend ? 1 : 0.5, cursor: canSend ? "pointer" : "not-allowed" }} data-testid="action-send-sms">
                    <MessageSquare style={{ width: 12, height: 12 }} /> Send quote via SMS
                  </button>
                  {["sent", "viewed"].includes(quote.status) && (
                    <>
                      <button onClick={handleAccept} disabled={actionLoading} className="btn" style={{
                        width: "100%", fontSize: 11, justifyContent: "flex-start", padding: "8px 12px",
                        background: "var(--green-light)", color: "var(--green)", border: "0.5px solid var(--green)"
                      }} data-testid="action-accept">
                        <CheckCircle2 style={{ width: 12, height: 12 }} /> Mark as accepted
                      </button>
                      <button onClick={() => updateStatus("reject")} disabled={actionLoading} className="btn" style={{
                        width: "100%", fontSize: 11, justifyContent: "flex-start", padding: "8px 12px",
                        color: "var(--red)"
                      }} data-testid="action-reject">
                        <XCircle style={{ width: 12, height: 12 }} /> Mark as rejected
                      </button>
                    </>
                  )}
                  {quote.status === "draft" && (
                    <button onClick={() => updateStatus("send")} disabled={actionLoading} className="btn btn-primary" style={{
                      width: "100%", fontSize: 11, justifyContent: "flex-start", padding: "8px 12px"
                    }} data-testid="action-mark-sent">
                      <Send style={{ width: 12, height: 12 }} /> Mark as sent
                    </button>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Accept prompt */}
          {showAcceptPrompt && (
            <div style={{ padding: 14, background: "var(--green-light)", borderRadius: 8, border: "0.5px solid var(--green)", marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--green)", marginBottom: 8 }}>Move this lead to Closed Won?</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => confirmAccept(true)} className="btn btn-primary" style={{ fontSize: 10, padding: "5px 10px", background: "var(--green)" }} data-testid="confirm-move-lead">
                  Yes, move
                </button>
                <button onClick={() => confirmAccept(false)} className="btn" style={{ fontSize: 10, padding: "5px 10px" }}>
                  Not yet
                </button>
              </div>
            </div>
          )}

          {/* Quote value card */}
          <div style={{ padding: 14, background: "var(--surface)", borderRadius: 8, border: "0.5px solid var(--border)" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)", marginBottom: 4 }}>${(quote.total || 0).toLocaleString()}</div>
            <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
              {(quote.line_items || []).length} line items &middot; {quote.contact_company || "No company"}
            </div>
            {/* Product pills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 8 }}>
              {(quote.line_items || []).slice(0, 4).map((item, i) => (
                <span key={i} style={{ fontSize: 8, fontWeight: 600, padding: "2px 6px", borderRadius: 3, background: "var(--blue-light)", color: "var(--blue)" }}>
                  {item.product}
                </span>
              ))}
              {(quote.line_items || []).length > 4 && (
                <span style={{ fontSize: 8, fontWeight: 600, padding: "2px 6px", borderRadius: 3, background: "var(--gray-light)", color: "var(--gray)" }}>
                  +{(quote.line_items || []).length - 4} more
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
